import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, ChevronLeft, ChevronRight, Check } from "lucide-react";
import { SEGMENTS } from "./shared";
import { ContentFields } from "./ContentFields";
import { EmailPreview } from "./EmailPreview";

const STEPS = ["Nom & modèle", "Contenu", "Ciblage", "Récapitulatif"];
const EMPTY = { name: "", theme: "default", subject: "🙏 Allumez une bougie du souvenir", body_html: "<p>BougieDuCœur vous permet d'allumer une bougie du souvenir en hommage à un proche disparu.</p>", button_label: "Découvrir · Inscription gratuite", target_url: "/", segments: { prospects: true, members: false, customers: false, expired: false } };

export const CampaignWizard = ({ open, onOpenChange, onCreated }) => {
  const [step, setStep] = useState(0);
  const [tpls, setTpls] = useState([]);
  const [d, setD] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [counts, setCounts] = useState(null);

  useEffect(() => {
    if (!open) return;
    setStep(0);
    api.get("/admin/email-campaigns/segment-counts").then((r) => setCounts(r.data)).catch(() => setCounts(null));
    api.get("/admin/email-templates").then((r) => {
      setTpls(r.data || []);
      const list = r.data || [];
      const t = list.find((x) => x.id === "tpl-toussaint") || list[0];
      setD({ ...EMPTY, ...(t ? { theme: t.theme || "default", subject: t.subject, body_html: t.body_html, button_label: t.button_label, target_url: t.target_url } : {}), tplId: t?.id });
    }).catch(() => setD(EMPTY));
  }, [open]);

  const pickTpl = (t) => setD({ ...d, tplId: t.id, theme: t.theme || "default", subject: t.subject, body_html: t.body_html, button_label: t.button_label, target_url: t.target_url });
  const toggleSeg = (k) => setD({ ...d, segments: { ...d.segments, [k]: !d.segments[k] } });
  const total = counts ? SEGMENTS.reduce((n, s) => n + (d.segments[s.key] ? counts[s.key] || 0 : 0), 0) - (d.segments.prospects && (d.segments.members || d.segments.customers || d.segments.expired) ? counts.sets_overlap || 0 : 0) : null;
  const canNext = step === 0 ? d.name.trim() : step === 1 ? d.subject.trim() && d.body_html.trim() : step === 2 ? Object.values(d.segments).some(Boolean) : true;

  const save = async (launch) => {
    setBusy(true);
    try {
      const { tplId, ...payload } = d;
      const r = await api.post("/admin/email-campaigns", payload);
      if (launch) {
        const l = await api.post(`/admin/email-campaigns/${r.data.id}/launch`);
        toast.success(`Campagne lancée${l.data.members_added ? ` · ${l.data.members_added} membre(s) ajouté(s)` : ""}`);
      } else toast.success("Campagne enregistrée en brouillon");
      onOpenChange(false); onCreated?.(r.data.id);
    } catch (e) { toast.error(e.response?.data?.detail || "Création impossible"); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92dvh] max-w-3xl overflow-y-auto" data-testid="campaign-wizard">
        <DialogHeader><DialogTitle>Nouvelle campagne e-mail</DialogTitle></DialogHeader>
        <div className="mb-3 grid grid-cols-4 gap-1.5">
          {STEPS.map((s, i) => (
            <div key={s} className={`rounded-full px-2 py-1 text-center text-[11px] font-medium ${i === step ? "bg-fuchsia-500 text-white" : i < step ? "bg-fuchsia-500/25 text-fuchsia-200" : "bg-white/5 text-muted-foreground"}`} data-testid={`wizard-step-${i}`}>{i + 1}. {s}</div>
          ))}
        </div>

        {step === 0 && (
          <div className="space-y-3">
            <div><Label className="text-xs">Nom de la campagne</Label><Input value={d.name} onChange={(e) => setD({ ...d, name: e.target.value })} placeholder="Ex. Toussaint 2026 — prospects" data-testid="campaign-name-input" /></div>
            <Label className="text-xs">Partir d'un modèle</Label>
            <div className="grid gap-2 sm:grid-cols-3">
              {tpls.map((t) => (
                <button key={t.id} type="button" onClick={() => pickTpl(t)} className={`rounded-xl border p-3 text-left text-xs transition-colors ${d.tplId === t.id ? "border-fuchsia-400 bg-fuchsia-500/15" : "border-white/10 bg-white/[0.03] hover:border-white/30"}`} data-testid={`wizard-tpl-${t.id}`}>
                  <p className="font-semibold text-white">{t.name}</p><p className="mt-1 truncate text-muted-foreground">{t.subject}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="grid gap-4 md:grid-cols-2">
            <ContentFields value={d} onChange={setD} prefix="wizard" />
            <EmailPreview draft={d} height={380} />
          </div>
        )}

        {step === 2 && (
          <div className="space-y-2" data-testid="wizard-segments">
            <p className="text-xs text-muted-foreground">Cochez les publics à qui envoyer cette campagne. Les désinscrits et les membres qui ont refusé les notifications sont toujours exclus, et chaque adresse ne reçoit l'e-mail qu'une fois.</p>
            {SEGMENTS.map((s) => (
              <label key={s.key} className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition-colors ${d.segments[s.key] ? "border-fuchsia-400/60 bg-fuchsia-500/10" : "border-white/10 bg-white/[0.03]"}`}>
                <input type="checkbox" checked={!!d.segments[s.key]} onChange={() => toggleSeg(s.key)} className="mt-0.5 h-4 w-4 accent-fuchsia-500" data-testid={`segment-${s.key}`} />
                <span className="flex-1"><span className="block text-sm font-medium text-white">{s.label}</span><span className="text-xs text-muted-foreground">{s.hint}</span></span>
                {counts && <span className="shrink-0 rounded-full bg-sky-500/15 px-2.5 py-0.5 text-xs font-bold text-sky-300" data-testid={`segment-count-${s.key}`}>{(counts[s.key] || 0).toLocaleString("fr-FR")}</span>}
              </label>
            ))}
            {total != null && <p className="rounded-xl border border-sky-400/30 bg-sky-500/10 p-3 text-sm text-sky-200" data-testid="wizard-total">Total : <strong>{total.toLocaleString("fr-FR")}</strong> e-mail(s) à envoyer · environ {Math.max(1, Math.ceil(total / 4990))} jour(s) à 4 990 par jour</p>}
          </div>
        )}

        {step === 3 && (
          <div className="space-y-2 text-sm" data-testid="wizard-summary">
            <p><span className="text-muted-foreground">Campagne :</span> <strong>{d.name}</strong></p>
            <p><span className="text-muted-foreground">Objet :</span> {d.subject}</p>
            <p><span className="text-muted-foreground">Bouton :</span> {d.button_label} → {d.target_url || "/"}</p>
            <p><span className="text-muted-foreground">Cibles :</span> {SEGMENTS.filter((s) => d.segments[s.key]).map((s) => s.label).join(", ")}</p>
            {total != null && <p data-testid="wizard-summary-total"><span className="text-muted-foreground">E-mails à envoyer :</span> <strong>{total.toLocaleString("fr-FR")}</strong> (≈ {Math.max(1, Math.ceil(total / 4990))} jour(s))</p>}
            <p className="rounded-xl border border-amber-300/30 bg-amber-300/10 p-3 text-xs text-amber-100">« Lancer » ajoute tout de suite les membres du site cochés. Les prospects s'importent ensuite depuis la carte de la campagne. L'envoi se fait automatiquement, par lots.</p>
          </div>
        )}

        <div className="mt-4 flex items-center justify-between gap-2">
          <Button variant="ghost" className="rounded-full" disabled={step === 0} onClick={() => setStep(step - 1)} data-testid="wizard-prev"><ChevronLeft size={15} className="mr-1" /> Retour</Button>
          {step < 3 ? (
            <Button className="rounded-full bg-fuchsia-500 hover:bg-fuchsia-400" disabled={!canNext} onClick={() => setStep(step + 1)} data-testid="wizard-next">Suivant <ChevronRight size={15} className="ml-1" /></Button>
          ) : (
            <div className="flex gap-2">
              <Button variant="outline" className="rounded-full" disabled={busy} onClick={() => save(false)} data-testid="wizard-save-draft">Enregistrer en brouillon</Button>
              <Button className="rounded-full bg-emerald-500 text-black hover:bg-emerald-400" disabled={busy} onClick={() => save(true)} data-testid="wizard-launch">{busy ? <Loader2 size={15} className="mr-1 animate-spin" /> : <Check size={15} className="mr-1" />} Lancer</Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
